/**
 * District Cache - Snapshot-cache för distrikt-state
 * 
 * ANSVAR:
 * - Caching av distrikt-state snapshots när man växlar mellan distrikt
 * - Tracking av dirty distrikt
 * 
 * DESIGN:
 * - React state är single source of truth för UI
 * - Denna cache är bara en snapshot - läser från React state vid save, skriver tillbaka vid load
 * - Ingen dubbel synkronisering - React state styr allt
 * 
 * INTE ANSVAR FÖR:
 * - UI-rendering
 * - Map-rendering
 * - API-anrop (men kan hämta från API om cache saknas)
 */

// Cache state (closure)
let cache = {}; // Snapshot cache: { districtId: { district, selectedAreas, ... } }
let currentDistrictId = null; // ID för det nuvarande distriktet
let classificationsCache = {}; // { scenarioId: [classifications] }
let subscribers = [];

// Dependencies (sätts via setters)
let mapService = null; // MapService för API-anrop
let unitsService = null; // UnitsService för API-anrop
let type = null; // Type (livs/foodservice)
let scenarioId = null; // Scenario ID
let fetchUnitsCallback = null; // Callback för att hämta units

/**
 * Sätt dependencies
 * @param {Object} dependencies - { mapService, unitsService, type, scenarioId, fetchUnitsCallback }
 */
export function setDependencies({ mapService: ms, unitsService: us, type: t, scenarioId: sid, fetchUnitsCallback: fetchUnits }) {
    mapService = ms;
    unitsService = us;
    type = t;
    scenarioId = sid;
    fetchUnitsCallback = fetchUnits;
}

/**
 * Hämta distrikt-state snapshot från cache.
 * @param {string} districtId - District ID
 * @returns {Object|null} District state snapshot eller null om det inte finns
 */
export function get(districtId) {
    return cache[districtId] || null;
}

/**
 * Sätt distrikt-state snapshot i cache
 * @param {string} districtId - District ID
 * @param {Object} districtState - District state snapshot att spara
 */
export function set(districtId, districtState) {
    cache[districtId] = districtState;
}

/**
 * Spara snapshot av nuvarande distrikt
 * @param {Object} district - District object to save
 */
export function saveCurrent(district) {
    // Sätt currentDistrictId när distrikt sparas
    if (district.id) {
        currentDistrictId = district.id;
    }
    
    set(district.id, district);

    return district;
}

/**
 * Hämta nuvarande distrikt-state
 * @returns {Object|null} Current district state eller null
 */
export function getCurrent() {
    if (!currentDistrictId) return null;
    return get(currentDistrictId);
}


/**
 * Ta bort distrikt från cache
 * @param {string|number} districtId - District ID att ta bort
 */
export function remove(districtId) {
    delete cache[String(districtId)];
    if (currentDistrictId === String(districtId)) {
        currentDistrictId = null;
    }
}

/**
 * Subscribe to cache changes. Returns unsubscribe function.
 */
export function subscribe(cb) {
    subscribers.push(cb);
    return () => { subscribers = subscribers.filter(s => s !== cb); };
}

export function notifySubscribers() {
    for (const cb of subscribers) cb();
}

/**
 * Rensa cache (användbart för testing eller reset)
 */
export function clear() {
    cache = {};
    currentDistrictId = null;
    classificationsCache = {};
}

/**
 * Hämta alla cached districts (för debugging)
 * @returns {Object} Alla cached districts { districtId: districtState, ... }
 */
export function getAllCache() {
    return { ...cache };
}

/**
 * Hämta alla district IDs i cache
 * @returns {Array} Array av district IDs
 */
export function getAllDistrictIds() {
    return Object.keys(cache);
}

/**
 * Hämta alla cached districts (för Report och SalesDistrictsManager)
 * @returns {Array} Array av district states
 */
export function getAllDistrictsForScenario() {
    return Object.values(cache);
}

/**
 * Hämta district state för ett distrikt baserat på ID
 * @param {string|number} districtId - District ID
 * @returns {Object|null} District state eller null
 */
export function getDistrictById(districtId) {
    return get(String(districtId));
}

/**
 * Hämta current district ID
 * @returns {string|null} Current district ID
 */
export function getCurrentDistrictId() {
    return currentDistrictId;
}

/**
 * Hämta selected areas för current district
 * @returns {Array} Array av GeoJSON features eller tom array
 */
export function getCurrentDistrictSelectedAreas() {
    if (!currentDistrictId) {
        return [];
    }
    const district = get(currentDistrictId);
    return district?.selectedAreas || [];
}

/**
 * Hämta selectionType för current district
 * @returns {string} Selection type eller 'clear' om inget finns
 */
export function getCurrentDistrictSelectionType() {
    if (!currentDistrictId) {
        return 'clear';
    }
    const district = get(currentDistrictId);
    return district?.selectionType || 'clear';
}

/**
 * Sätt classifications för ett scenario
 * @param {string} scenarioId - Scenario ID
 * @param {Array} classifications - Array av classifications
 */
export function setClassifications(scenarioId, classifications) {
    classificationsCache[scenarioId] = classifications;
}

/**
 * Hämta classifications för ett scenario
 * @param {string} scenarioId - Scenario ID
 * @returns {Array} Array av classifications
 */
export function getClassifications(scenarioId) {
    return classificationsCache[scenarioId] || [];
}

/**
 * Hämta district types från det nuvarande distriktets parsed layers
 * @returns {Array} Array av district types (t.ex. ['zip1', 'municipality'])
 */
export function getCurrentDistrictTypes() {
    if (!currentDistrictId) {
        return [];
    }
    
    const district = get(currentDistrictId);
    if (!district) {
        return [];
    }
    
    // Dynamisk import för att undvika circular dependency
    const { parseSelectedLayers } = require('../utils/zip-code-parser');
    const parsedLayers = parseSelectedLayers(district);
    const parsedLayersKeys = Object.keys(parsedLayers).filter(key => parsedLayers[key].length > 0);
    
    return parsedLayersKeys;
}
